import React from 'react';
import { Calendar, Users as _Users, DollarSign, ArrowRight, Users } from 'lucide-react';

/**
 * Alternative Dates Panel
 * Shows alternative available dates when selected date is unavailable
 *
 * @param {Object} props
 * @param {Array} props.suggestions - Array of alternative date suggestions
 * @param {Function} props.onSelectDate - Handler when user selects alternative date
 * @param {Function} props.onContactGuide - Handler when user wants to contact guide
 * @param {string} props.basePrice - Base price per person for reference
 */
export default function AlternativeDatesPanel({
  suggestions = [],
  onSelectDate,
  onContactGuide,
  basePrice
}) {
  if (suggestions.length === 0) {
    return (
      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="text-sm text-amber-900">
          <div className="font-semibold mb-2">No Alternative Dates Available</div>
          <p className="mb-3">
            Unfortunately, there are no similar dates available right now. You can try selecting a different date range, or contact the guide directly to discuss custom booking options.
          </p>
          <button
            onClick={onContactGuide}
            className="inline-flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-700 transition"
          >
            Contact Guide
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Format date to "Monday, June 17"
  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate distance from original date
  const _getDistanceLabel = (dateStr, originalDate) => {
    if (!originalDate) {return null;}

    const date = new Date(dateStr);
    const original = new Date(originalDate);
    const diffTime = date - original;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {return 'Same day';}
    if (diffDays === 1) {return '1 day later';}
    if (diffDays === -1) {return '1 day earlier';}
    if (diffDays > 0) {return `${diffDays} days later`;}
    return `${Math.abs(diffDays)} days earlier`;
  };

  return (
    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="text-sm text-blue-900 mb-3">
        <div className="font-semibold mb-1">Alternative Dates Available</div>
        <p className="text-xs">
          Your selected date is unavailable. Here are some nearby dates that are currently open:
        </p>
      </div>

      <div className="space-y-2">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => onSelectDate(suggestion.date)}
            className="w-full p-3 bg-white hover:bg-blue-50 border border-blue-200 hover:border-blue-400 rounded-lg transition text-left"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-gray-900">
                    {formatFullDate(suggestion.date)}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs text-gray-600 ml-6">
                  {/* Availability status */}
                  {suggestion.slots_available && (
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>
                        {suggestion.slots_available <= 3
                          ? `Limited (${suggestion.slots_available} spots)`
                          : 'Available'}
                      </span>
                    </div>
                  )}

                  {/* Distance from original date */}
                  {suggestion.distance && (
                    <div className="flex items-center gap-1">
                      <ArrowRight className="w-3 h-3" />
                      <span>{suggestion.distance}</span>
                    </div>
                  )}

                  {/* Variable pricing if applicable */}
                  {suggestion.price_per_person && suggestion.price_per_person !== basePrice && (
                    <div className="flex items-center gap-1 text-green-700 font-semibold">
                      <DollarSign className="w-3 h-3" />
                      <span>${suggestion.price_per_person}/person</span>
                    </div>
                  )}
                </div>
              </div>

              <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-blue-200">
        <button
          onClick={onContactGuide}
          className="text-xs text-blue-700 hover:text-blue-900 font-semibold transition"
        >
          Still don&apos;t see what you&apos;re looking for? Contact the guide →
        </button>
      </div>
    </div>
  );
}
